import React, { useState, useEffect, useCallback } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useLocation } from 'react-router-dom';
import { locationHubService } from '../../services/locationHubService';
import companyService from '../../services/CompanyService';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert';
import { MapPin, ArrowLeft } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import StateSelectField from '../common/StateSelectField.js';
import { hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import HubGpsFields, { fillCurrentGps } from './HubGpsFields.js';
import DestinationHubMultiSelect from './DestinationHubMultiSelect.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import HubDeliverySettings, { composeDeliveryTime } from './HubDeliverySettings.js';
import HubPrintSettings from './HubPrintSettings.js';
import companySettingsService from '../../services/companySettingsService.js';
import { isLrShipmentFormat } from '../../utils/shipmentIdFormat.js';

const AddLocationHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialDestinationType = location.state?.destinationType === 'hub_destination'
    ? 'hub_destination'
    : 'branch_destination';
  const { user } = useAuth();
  const isSystemAdmin = user?.role === 'System Admin';
  const showDispatchControls = isSystemAdmin || hasInternalHubDispatchControls(user);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [parentHubOptions, setParentHubOptions] = useState([]);
  const [destinationHubOptions, setDestinationHubOptions] = useState([]);
  const [showLrBranchCode, setShowLrBranchCode] = useState(false);
  const [branchUpiEnabled, setBranchUpiEnabled] = useState(Boolean(user?.branch_upi_qr_enabled));
  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    company_id:             (v) => isSystemAdmin ? validators.required(v, 'Company') : '',
    address:                (v) => validators.required(v, 'Address'),
    location:               (v) => validators.required(v, 'Hub/Branch'),
    state:                  (v) => validators.required(v, 'State'),
    location_code:          (v) => validators.required(v, 'Hub/Branch code'),
    lr_branch_code:         (v) => {
      if (!showLrBranchCode) return ''
      const text = String(v || '').trim()
      if (!text) return 'LR branch code is required'
      if (!/^\d{2}$/.test(text)) return 'LR branch code must be 2 digits (00-99)'
      return ''
    },
    location_hub_contact:   (v) => validators.required(v, 'Branch Contact Number') || validators.contactVarchar(v),
    branch_upi_id:          (v) => {
      const text = String(v || '').trim();
      if (!text) return '';
      if (!text.includes('@') || text.length > 100) return 'Enter a valid UPI ID (e.g. branch@okaxis)';
      return '';
    },
    commission_percentage:  (v) => v ? validators.number(v, { min: 0, max: 100, label: 'Commission' }) : '',
    labour_reserve_percent: (v) => v ? validators.number(v, { min: 0, max: 100, label: 'Labour reserve' }) : '',
    loading_labour_reserve_percent: (v) => v ? validators.number(v, { min: 0, max: 100, label: 'Loading reserve' }) : '',
    unloading_labour_reserve_percent: (v) => v ? validators.number(v, { min: 0, max: 100, label: 'Unloading reserve' }) : '',
    rate_per_kg:            (v) => v ? validators.number(v, { min: 0, label: 'Rate / KG' }) : '',
    parent_hub_id:          () => '',
    destination_location_hub_ids: (v) => (
      Array.isArray(v) && v.length > 0 ? '' : 'Destination Location Hubs are required'
    ),
    settlement_location_hub_ids: (v) => (
      Array.isArray(v) && v.length > 0 ? '' : 'Settle to hubs is required'
    ),
    latitude: (v) => (String(v || '').trim() ? '' : 'Branch GPS latitude is required'),
    longitude: (v) => (String(v || '').trim() ? '' : 'Branch GPS longitude is required'),
    dispatch_types:         (v) => showDispatchControls && (!Array.isArray(v) || v.length === 0)
      ? 'Select at least one dispatch type'
      : '',
  };

  const [formData, setFormData] = useState({
    company_id: '',
    address: '',
    location: '',
    state: '',
    location_code: '',
    lr_branch_code: '',
    destination_type: initialDestinationType,
    parent_hub_id: '',
    location_hub_contact: '',
    branch_upi_id: '',
    commission_percentage: '',
    labour_reserve_percent: '',
    loading_labour_reserve_percent: '',
    unloading_labour_reserve_percent: '',
    rate_per_kg: '',
    ownership_type: 'OWN',
    dispatch_types: ['OGCL'],
    allow_paid: true,
    allow_booking: true,
    allow_dispatch: false,
    allow_to_pay: true,
    allow_on_account: true,
    allow_delivery: true,
    latitude: '',
    longitude: '',
    destination_location_hub_ids: [],
    settlement_location_hub_id: "",
    settlement_location_hub_ids: [],
    delivery_time_1: "",
    delivery_period_1: "AM",
    delivery_time_2: "",
    delivery_period_2: "PM",
    delivery_time_legacy: "",
    delivery_details_required: true,
    booking_receipt_size: 'large',
    delivery_receipt_size: 'large',
    print_qr_with_booking_receipt: false,
    qr_print_template: 'small',
  });

  const isBranch = formData.destination_type === 'branch_destination';

  const loadParentHubs = useCallback(async (companyId) => {
    try {
      const filters = { destination_type: 'hub_destination' };
      if (companyId) filters.company_id = companyId;
      const response = await locationHubService.getHubsForSelect(filters);
      const hubs = response.data.hubs || response.data.data || [];
      setParentHubOptions(hubs);
    } catch (error) {
      console.error('Error loading parent hubs:', error);
      setParentHubOptions([]);
    }
  }, []);

  const loadDestinationHubs = useCallback(async (companyId) => {
    try {
      const filters = {};
      if (companyId) filters.company_id = companyId;
      const response = await locationHubService.getHubsForSelect(filters);
      const hubs = response.data.hubs || response.data.data || [];
      setDestinationHubOptions(hubs);
    } catch (error) {
      console.error('Error loading destination hubs:', error);
      setDestinationHubOptions([]);
    }
  }, []);

  useEffect(() => {
    if (isSystemAdmin) {
      companyService.getActiveCompanies()
        .then((res) => setCompanies(res.data || []))
        .catch(() => Alert.error('Failed to load companies'));
    } else if (user?.company_internal_id) {
      setFormData((prev) => ({
        ...prev,
        company_id: String(user.company_internal_id),
      }));
    }
  }, [isSystemAdmin, user?.company_internal_id]);

  useEffect(() => {
    if (!isBranch) {
      setParentHubOptions([]);
      return;
    }
    const companyId = isSystemAdmin
      ? (formData.company_id || null)
      : (user?.company_internal_id || null);
    if (isSystemAdmin && !companyId) {
      setParentHubOptions([]);
      return;
    }
    loadParentHubs(companyId);
  }, [isBranch, formData.company_id, isSystemAdmin, user?.company_internal_id, loadParentHubs]);

  useEffect(() => {
    const companyId = isSystemAdmin
      ? (formData.company_id || null)
      : (user?.company_internal_id || null);
    if (isSystemAdmin && !companyId) {
      setDestinationHubOptions([]);
      return;
    }
    loadDestinationHubs(companyId);
  }, [formData.company_id, isSystemAdmin, user?.company_internal_id, loadDestinationHubs]);

  useEffect(() => {
    const companyId = isSystemAdmin
      ? (formData.company_id || null)
      : (user?.company_internal_id || null);
    if (!companyId) {
      setShowLrBranchCode(false);
      return undefined;
    }
    let cancelled = false;
    companySettingsService.getSettings(companyId)
      .then((res) => {
        if (!cancelled) {
          setShowLrBranchCode(
            !!res?.data?.lr_branch_code_required
            || isLrShipmentFormat(res?.data?.shipment_id_format),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setShowLrBranchCode(false);
      });
    return () => { cancelled = true; };
  }, [formData.company_id, isSystemAdmin, user?.company_internal_id]);

  useEffect(() => {
    const companyId = isSystemAdmin
      ? (formData.company_id || null)
      : (user?.company_internal_id || null);
    if (!companyId) {
      setBranchUpiEnabled(Boolean(user?.branch_upi_qr_enabled));
      return undefined;
    }
    let cancelled = false;
    companyService.getRecord(companyId)
      .then((res) => {
        if (!cancelled) setBranchUpiEnabled(Boolean(res?.data?.branch_upi_qr_enabled));
      })
      .catch(() => {
        if (!cancelled) setBranchUpiEnabled(Boolean(user?.branch_upi_qr_enabled));
      });
    return () => { cancelled = true; };
  }, [formData.company_id, isSystemAdmin, user?.company_internal_id, user?.branch_upi_qr_enabled]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'location_hub_contact') filtered = inputFilters.phoneVarchar(value);
    if (name === 'commission_percentage' || name === 'labour_reserve_percent'
      || name === 'loading_labour_reserve_percent' || name === 'unloading_labour_reserve_percent') {
      filtered = inputFilters.amount(value);
    }
    if (name === 'rate_per_kg') filtered = inputFilters.amount(value);
    if (name === 'location' || name === 'location_code') filtered = value.toUpperCase();
    if (name === 'lr_branch_code') filtered = value.replace(/\D/g, '').slice(0, 2);

    setFormData((prev) => {
      const next = { ...prev, [name]: filtered };
      if (name === 'destination_type' && filtered !== 'branch_destination') {
        next.parent_hub_id = '';
      }
      if (name === 'company_id') {
        next.parent_hub_id = '';
        next.destination_location_hub_ids = [];
      }
      if (name === 'ownership_type' && filtered === 'AGENT') {
        next.allow_on_account = false;
      }
      return next;
    });

    if (validationRules[name]) {
      const nextAll = {
        ...formData,
        [name]: filtered,
        ...(name === 'destination_type' && filtered !== 'branch_destination'
          ? { parent_hub_id: '' }
          : {}),
      };
      validateField(name, filtered, (v) => validationRules[name](v, nextAll));
    }
  };

  const handleDispatchTypeChange = (type) => {
    setFormData((prev) => {
      const dispatch_types = prev.dispatch_types.includes(type)
        ? prev.dispatch_types.filter((item) => item !== type)
        : [...prev.dispatch_types, type];
      validateField('dispatch_types', dispatch_types, validationRules.dispatch_types);
      return { ...prev, dispatch_types };
    });
  };

  const handleHubToggle = (name) => {
    setFormData((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, {
      ...validationRules,
      parent_hub_id: (v) => validationRules.parent_hub_id(v, formData),
    })) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    const deliveryTime = composeDeliveryTime(
      { time: formData.delivery_time_1, period: formData.delivery_period_1 },
      { time: formData.delivery_time_2, period: formData.delivery_period_2 },
      formData.delivery_time_legacy,
    );
    if (deliveryTime.error) {
      Alert.error(deliveryTime.error);
      return;
    }

    try {
      setLoading(true);
      
      const submitData = {
        address: formData.address.trim(),
        location: formData.location.trim().toUpperCase(),
        state: formData.state,
        location_code: formData.location_code.trim().toUpperCase(),
        lr_branch_code: showLrBranchCode
          ? (String(formData.lr_branch_code || '').trim() || null)
          : null,
        destination_type: formData.destination_type,
        parent_hub_id: isBranch && formData.parent_hub_id
          ? parseInt(formData.parent_hub_id, 10)
          : null,
        location_hub_contact: formData.location_hub_contact
          ? String(formData.location_hub_contact).trim()
          : null,
        branch_upi_id: formData.branch_upi_id
          ? String(formData.branch_upi_id).trim()
          : null,
        commission_percentage: formData.commission_percentage !== '' ? parseInt(formData.commission_percentage) : null,
        loading_labour_reserve_percent: formData.loading_labour_reserve_percent !== '' ? parseFloat(formData.loading_labour_reserve_percent) : null,
        unloading_labour_reserve_percent: formData.unloading_labour_reserve_percent !== '' ? parseFloat(formData.unloading_labour_reserve_percent) : null,
        labour_reserve_percent: formData.loading_labour_reserve_percent !== ''
          ? parseFloat(formData.loading_labour_reserve_percent)
          : (formData.unloading_labour_reserve_percent !== '' ? parseFloat(formData.unloading_labour_reserve_percent) : null),
        rate_per_kg: formData.rate_per_kg !== '' ? parseFloat(formData.rate_per_kg) : null,
        ...(showDispatchControls ? {
          ownership_type: formData.ownership_type,
          dispatch_types: formData.dispatch_types,
          allow_paid: formData.allow_paid,
          allow_to_pay: formData.allow_to_pay,
          allow_on_account: formData.allow_on_account,
          allow_delivery: formData.allow_delivery,
          allow_booking: formData.allow_booking,
          allow_dispatch: formData.allow_dispatch,
        } : {}),
        latitude: formData.latitude === '' ? null : formData.latitude,
        longitude: formData.longitude === '' ? null : formData.longitude,
        settlement_location_hub_ids: (formData.settlement_location_hub_ids || []).map(Number),
        settlement_location_hub_id: (formData.settlement_location_hub_ids || [])[0]
          ? Number(formData.settlement_location_hub_ids[0])
          : null,
        delivery_time: deliveryTime.value || "",
        delivery_details_required: formData.delivery_details_required !== false,
        booking_receipt_size: formData.booking_receipt_size || 'large',
        delivery_receipt_size: formData.delivery_receipt_size || 'large',
        print_qr_with_booking_receipt: !!formData.print_qr_with_booking_receipt,
        qr_print_template: formData.qr_print_template === 'card_3inch' ? 'card_3inch' : 'small',
        destination_location_hub_ids: (formData.destination_location_hub_ids || [])
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id)),
      };

      await locationHubService.createHub(
        submitData,
        isSystemAdmin ? parseInt(formData.company_id, 10) : null,
      );
      
      Alert.success('Location hub created successfully!');
      navigate('/location-hubs');
      
    } catch (error) {
      console.error('Error creating hub:', error);
      Alert.error(error.response?.data?.error || 'Failed to create location hub');
    } finally {
      setLoading(false);
    }
  };

  const selectedParent = parentHubOptions.find(
    (hub) => String(hub.id) === String(formData.parent_hub_id)
  );

  const selectableDestinationHubs = destinationHubOptions;

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3">
                    <div className='flex w-full'>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                        <MapPin className="w-6 h-6 text-secondary" />
                        {isBranch ? 'Add Branch' : 'Add Hub'}
                      </h1>
                    </div>
                    <div className="flex justify-end w-full">
                      <div className="flex items-center gap-3 sm:gap-4 md:gap-6">
                        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/location-hubs')}>
                          Back to Location Hubs
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Hub Information
                    </h3>
                  </div>

                  <div className='grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6'>
                    {isSystemAdmin && (
                      <div className="col-span-2 sm:col-span-4">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Company <span className="text-red-500">*</span>
                        </label>
                        <SelectField variant="form"
                          name="company_id"
                          value={formData.company_id}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.company_id ? 'border-red-400' : 'border-gray-300'}`}
                          required
                          disabled={loading}
                        >
                          <option value="">Select company</option>
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.company_name} ({company.company_id})
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.company_id} />
                      </div>
                    )}

                    {/* Destination */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Hub/Branch <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Enter destination name"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.location ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.location} />
                    </div>

                    {/* Destination Code / Location Code */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Location Hub Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="location_code"
                        value={formData.location_code}
                        onChange={handleChange}
                        placeholder="e.g., MU for Mumbai"
                        maxLength="10"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base uppercase ${errors.location_code ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.location_code} />
                    </div>

                    {showLrBranchCode && (
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        LR branch code <span className="text-red-500">*</span>{' '}
                        <span className="text-gray-400 font-normal">(00-99)</span>
                      </label>
                      <input
                        type="text"
                        name="lr_branch_code"
                        value={formData.lr_branch_code}
                        onChange={handleChange}
                        placeholder="05"
                        maxLength="2"
                        inputMode="numeric"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.lr_branch_code ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <p className="mt-1 text-[11px] text-gray-400">Used in the number sequence. One code per branch. Shown as 05 – Salem on booking.</p>
                      <ValidationMessage error={errors.lr_branch_code} />
                    </div>
                    )}

                    {/* Parent Hub */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Parent Hub <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <SelectField variant="form"
                        name="parent_hub_id"
                        value={isBranch ? formData.parent_hub_id : ''}
                        onChange={handleChange}
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.parent_hub_id ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading || !isBranch || (isSystemAdmin && !formData.company_id)}
                      >
                        <option value="">
                          {!isBranch
                            ? 'Not used for Hub'
                            : isSystemAdmin && !formData.company_id
                              ? 'Select company first'
                              : parentHubOptions.length === 0
                                ? 'No hubs available'
                                : 'Select parent hub'}
                        </option>
                        {isBranch && sortLocationsAsc(parentHubOptions).map((hub) => (
                          <option key={hub.id} value={hub.id}>
                            {formatLocationDropdownLabel(hub, showLrBranchCode)}
                          </option>
                        ))}
                      </SelectField>
                      <ValidationMessage error={errors.parent_hub_id} />
                      {isBranch && selectedParent && (
                        <p className="mt-1 text-xs text-gray-500">
                          Parent location code: <span className="font-mono font-semibold">{selectedParent.location_code}</span>
                        </p>
                      )}
                    </div>

                    {/* Destination Type */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Destination Type <span className="text-red-500">*</span>
                      </label>
                      <SelectField variant="form"
                        name="destination_type"
                        value={formData.destination_type}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        disabled={loading}
                      >
                        <option value="branch_destination">Branch</option>
                        <option value="hub_destination">Hub</option>
                      </SelectField>
                    </div>

                    {/* Commission % — hidden for now
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Commission %
                      </label>
                      <input
                        type="text"
                        name="commission_percentage"
                        value={formData.commission_percentage}
                        onChange={handleChange}
                        placeholder="0 - 100"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.commission_percentage ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.commission_percentage} />
                    </div>
                    */}

                    {!!user?.labour_reserve_enabled && (
                    <>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Loading reserve %
                      </label>
                      <input
                        type="text"
                        name="loading_labour_reserve_percent"
                        value={formData.loading_labour_reserve_percent}
                        onChange={handleChange}
                        placeholder="0 - 100"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.loading_labour_reserve_percent ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <p className="mt-1 text-[11px] text-gray-400">Booking agent at this hub. Advance % paid to cashbook on ML create; remainder held as reserve.</p>
                      <ValidationMessage error={errors.loading_labour_reserve_percent} />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Unloading reserve %
                      </label>
                      <input
                        type="text"
                        name="unloading_labour_reserve_percent"
                        value={formData.unloading_labour_reserve_percent}
                        onChange={handleChange}
                        placeholder="0 - 100"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.unloading_labour_reserve_percent ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <p className="mt-1 text-[11px] text-gray-400">Delivery agent at this hub. Advance % paid to cashbook on ML create; remainder held as reserve.</p>
                      <ValidationMessage error={errors.unloading_labour_reserve_percent} />
                    </div>
                    </>
                    )}

                    {/* Address */}
                    <div className="col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter complete address"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.address ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.address} />
                    </div>

                    <StateSelectField
                      label="State"
                      required
                      disabled={loading}
                      value={formData.state}
                      error={errors.state}
                      onChange={(state) => {
                        setFormData({ ...formData, state });
                        validateField('state', state, validationRules.state);
                      }}
                    />

                    {/* Contact */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Branch Contact Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="location_hub_contact"
                        value={formData.location_hub_contact}
                        onChange={handleChange}
                        placeholder="Enter contact number"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.location_hub_contact ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.location_hub_contact} />
                    </div>

                    {branchUpiEnabled && (
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Branch UPI ID
                      </label>
                      <input
                        type="text"
                        name="branch_upi_id"
                        value={formData.branch_upi_id}
                        onChange={handleChange}
                        placeholder="branch@okaxis"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.branch_upi_id ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                        autoComplete="off"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Optional. Used when Branch UPI QR is enabled on the company. Payment on this UPI still settles.
                      </p>
                      <ValidationMessage error={errors.branch_upi_id} />
                    </div>
                    )}

                    {/* Destination location hubs */}
                    <div className="col-span-2 sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Destination Location Hubs <span className="text-red-500">*</span>
                      </label>
                      <DestinationHubMultiSelect
                        useNumberCode={showLrBranchCode}
                        hubs={selectableDestinationHubs}
                        value={formData.destination_location_hub_ids}
                        onChange={(ids) => setFormData((prev) => ({
                          ...prev,
                          destination_location_hub_ids: ids,
                        }))}
                        disabled={loading || (isSystemAdmin && !formData.company_id)}
                        placeholder={
                          isSystemAdmin && !formData.company_id
                            ? 'Select company first'
                            : selectableDestinationHubs.length === 0
                              ? 'No hubs available'
                              : 'Select destination hubs'
                        }
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Only these hubs (and their branches) appear in Booking / ML To when this hub is the source.
                      </p>
                      <ValidationMessage error={errors.destination_location_hub_ids} />
                    </div>

                    {/* Rate / KG */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Rate / KG (₹) <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        name="rate_per_kg"
                        value={formData.rate_per_kg}
                        onChange={handleChange}
                        placeholder="e.g. 4.5"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.rate_per_kg ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <p className="mt-1 text-xs text-gray-400">Used for weight-based freight when this hub is the receiver</p>
                      <ValidationMessage error={errors.rate_per_kg} />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Settle to hubs <span className="text-red-500">*</span>
                      </label>
                      <DestinationHubMultiSelect
                        useNumberCode={showLrBranchCode}
                        hubs={destinationHubOptions}
                        value={formData.settlement_location_hub_ids}
                        onChange={(ids) => setFormData((prev) => ({
                          ...prev,
                          settlement_location_hub_ids: ids,
                        }))}
                        placeholder="Tick at least one hub"
                      />
                      <p className="mt-1 text-[11px] text-gray-400">
                        Tick the hub this branch settles to. One hub is already selected when the agent settles. Several hubs let the agent pick one.
                      </p>
                      <ValidationMessage error={errors.settlement_location_hub_ids} />
                    </div>
                    <HubPrintSettings
                      bookingReceiptSize={formData.booking_receipt_size}
                      deliveryReceiptSize={formData.delivery_receipt_size}
                      printQr={formData.print_qr_with_booking_receipt}
                      qrTemplate={formData.qr_print_template}
                      disabled={loading}
                      onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
                    />
                    <HubDeliverySettings
                      deliveryTime1={formData.delivery_time_1}
                      period1={formData.delivery_period_1}
                      deliveryTime2={formData.delivery_time_2}
                      period2={formData.delivery_period_2}
                      legacyText={formData.delivery_time_legacy}
                      detailsRequired={formData.delivery_details_required}
                      disabled={loading}
                      onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
                    />
                    <HubGpsFields
                      formData={formData}
                      onChange={handleChange}
                      loading={loading}
                      onUseCurrent={() => fillCurrentGps((coords) => setFormData((prev) => ({ ...prev, ...coords })))}
                    />

                    {showDispatchControls && (
                      <div className="col-span-2 sm:col-span-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <h4 className="font-semibold text-secondary mb-3">Hub Dispatch Capabilities</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Ownership Type</label>
                            <SelectField variant="form" name="ownership_type" value={formData.ownership_type} onChange={handleChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded-xl">
                              <option value="OWN">Own</option>
                              <option value="AGENT">Agent</option>
                            </SelectField>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-xs sm:text-sm font-medium text-secondary mb-2">Dispatch Types <span className="text-red-500">*</span></p>
                            <div className="flex gap-5">
                              {['OGCL', 'ITCL'].map((type) => (
                                <label key={type} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={formData.dispatch_types.includes(type)} onChange={() => handleDispatchTypeChange(type)} disabled={loading} />
                                  {type}
                                </label>
                              ))}
                            </div>
                            <ValidationMessage error={errors.dispatch_types} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
                          {[
                            ['allow_paid', 'Allow Paid'],
                            ['allow_to_pay', 'Allow To Pay'],
                            ['allow_on_account', 'Allow Credit Booking'],
                            ['allow_delivery', 'Allow Delivery'],
                            ['allow_booking', 'Allow Booking'],
                            ['allow_dispatch', 'Allow Dispatch'],
                          ].map(([name, label]) => (
                            <label key={name} className="flex items-center gap-2 text-sm text-gray-700">
                              <input type="checkbox" checked={formData[name]} onChange={() => handleHubToggle(name)} disabled={loading} />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" variant="primary" disabled={loading} loading={loading}>
                      {loading ? 'Creating...' : 'Create Hub'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/location-hubs')} disabled={loading}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default AddLocationHub;
